import { css } from 'styled-components';
import urls from './urls';

const baseColors = {
  // Calculation and css hsla() string generator
  hsla: (color, adjS = 0, adjL = 0, alpha = 1.0) =>
    `hsla(${color[0]}, ${Math.max(color[1] + adjS, 0)}%, ${Math.min(
      color[2] + adjL,
      100,
    )}%, ${alpha})`,

  primary: [143, 44, 50], // Green
  primaryHighContrast: [143, 44, 36], // Darker green
  secondary: [216, 53.7, 44.9], // Blue
  tertiary: [181, 100, 32], // Teal
  white: '#fff',
  black: '#050505',
  darkGrey: [0, 0, 29],
  lightGrey: [0, 0, 97],
  facebook: [229, 36, 43],
  linkedin: [201, 100, 35],
  apple: [255, 255, 255],
  danger: [348, 83.3, 47.1],
  warning: [41, 94, 52],
};

const brandColors = {
  base: baseColors,
  light: {
    ...baseColors,

    background: '#eee', // Major backgrounds such as the entire page
    lightestBackground: '#fff',
    lighterBackground: '#efefef',
    darkerBackground: '#d5d5d5',
    darkestBackground: '#ccc',
    backgroundImportant: baseColors.hsla(baseColors.primary),
    backgroundHighlight: baseColors.hsla(baseColors.tertiary),
    backgroundHighlightAlt: baseColors.hsla(baseColors.secondary),
    backgroundDanger: baseColors.hsla(baseColors.danger),

    links: baseColors.hsla(baseColors.secondary),
    linksInverse: baseColors.hsla(baseColors.primary, 25, 35),
    disabledInputText: '#666',
    primaryText: '#222',
    primaryTextImportant: baseColors.hsla(baseColors.primary),
    primaryTextHighlight: baseColors.hsla(baseColors.tertiary),
    primaryTextDanger: baseColors.hsla(baseColors.danger),
    primaryTextWarning: baseColors.hsla([24, 100, 50]),
    primaryTextInverse: baseColors.white,
    secondaryText: '#777',
    secondaryTextInverse: '#777',
    alternateText: '#3567b0',
    alternateTextInverse: '#555',
    statusColors: {
      error: baseColors.hsla([348, 83.3, 47.1]),
      warning: baseColors.hsla(baseColors.warning),
      notice: baseColors.hsla([216, 54, 90]),
      success: baseColors.hsla([181, 62.5, 52.4]),
      default: baseColors.white,
    },
    // New theme options (Q1 2023)
    inputBackground: '#fff',
    inputBorder: '',
    inputText: '',
    placeholders: '#bcbcbc',

    collectionItemBackground: 'transparent',
    collectionItemAltBackground: '#f9f9f9', // For zebra-striping of rows
    collectionItemBorder: '',
    collectionItemText: '',
    collectionItemLinks: '',
    collectionItemHighlightBackground: '',
    collectionItemHighlightText: '',
    collectionItemHighlightLinks: '',

    panelBackground: baseColors.white,
    panelBorder: 'rgba(0,0,0,0.1)',
    panelText: '#222',
    sectionBackground: '', // Management sections content areas, etc
    subSectionBackground: '', // Modules inside of sections (Address book, etc)
    subSectionHeaderBackground: '',
    subSectionHeaderText: '',
    navigationBackground: baseColors.hsla([220, 55, 15]), // Dark blue

    // Header tiers (management)
    primaryInformationBackground: '',
    primaryInformationText: '',
    primaryInformationLinks: '',
    secondaryInformationBackground: '',
    secondaryInformationText: '',
    secondaryInformationLinks: '',
    tertiaryInformationBackground: '',
    tertiaryInformationText: '',
    tertiaryInformationLinks: '',

    // Buttons
    primaryActionBackground: '',
    primaryActionText: '',
    secondaryActionBackground: '',
    secondaryActionText: '',
    tertiaryActionBackground: '',
    tertiaryActionText: '',

    subtleActionBackground: '',
    subtleActionBorder: '',
    subtleActionText: '#00001c',

    subtleActionDangerBackground: '#cf3e40',
    subtleActionDangerHoverBackground: '#e64345',
    subtleActionDangerBorder: '',
    subtleActionDangerText: 'white',

    subtleActionSuccessBackground: '',
    subtleActionSuccessBorder: '',
    subtleActionSuccessText: '',
  },
  dark: {
    ...baseColors,
    navigationBackground: baseColors.secondary, // Dark blue
    lightestBackground: '#333',
    lighterBackground: '#222',
    background: '#111',
    darkerBackground: '#080808',
    darkestBackground: '#030303',
    backgroundImportant: baseColors.hsla(baseColors.primary),
    backgroundHighlight: baseColors.hsla(baseColors.tertiary),
    backgroundHighlightAlt: baseColors.hsla(baseColors.secondary),
    backgroundDanger: baseColors.hsla(baseColors.danger),
    placeholders: '#bcbcbc',
    links: baseColors.hsla(baseColors.secondary, 10, 25),
    linksInverse: baseColors.hsla(baseColors.primary, 25, 35),
    disabledInputText: '#666',
    primaryText: '#ddd',
    primaryTextImportant: baseColors.hsla(baseColors.primary),
    primaryTextHighlight: baseColors.hsla(baseColors.tertiary),
    primaryTextDanger: baseColors.hsla(baseColors.danger),
    primaryTextInverse: baseColors.black,
    secondaryText: '#777',
    secondaryTextInverse: '#777',
    alternateText: '#3567b0',
    alternateTextInverse: '#555',
    statusColors: {
      error: baseColors.hsla([348, 83.3, 47.1]),
      warning: baseColors.hsla([34, 100, 44]),
      notice: baseColors.hsla([216, 54, 90]),
      success: baseColors.hsla([181, 62.5, 52.4]),
      default: '#222',
    },
  },
};

const baseLine = 22;
const bm = (m) => `${baseLine * m}px`;
const rhythm = [bm(0.25), bm(0.5), bm(1), bm(2), bm(3), bm(4), bm(5)];

const boxWidths = {
  xxs: bm(0.75),
  xs: bm(1),
  sm: bm(2),
  md: bm(3),
  lg: bm(4),
  xl: bm(8),
  xxl: bm(10),
  flex: '100%',
};

const margins = {
  zero: 0,
  xxs: rhythm[0],
  xs: rhythm[1],
  sm: rhythm[2],
  md: rhythm[3],
  lg: rhythm[4],
  xl: rhythm[5],
};

const screenSizes = {
  larger: 1061,
  desktop: 1060,
  tablet: 880,
  phone: 435,
};

const fontWeights = {
  light: 400,
  medium: 400,
  heavy: 600,
};

const fonts = {
  primary: {
    fontFamily: `'Fira Sans', sans-serif`,
    fontWeight: fontWeights.light,
    '-webkit-font-smoothing': 'antialiased',
    '-moz-osx-font-smoothing': 'grayscale',
  },
  primaryItalic: {
    fontFamily: `'Fira Sans', sans-serif`,
    fontStyle: 'italic',
    fontWeight: fontWeights.light,
    '-webkit-font-smoothing': 'antialiased',
    '-moz-osx-font-smoothing': 'grayscale',
  },
  secondary: {
    fontFamily: `'Kalam', cursive`,
    fontWeight: fontWeights.light,
    '-webkit-font-smoothing': 'antialiased',
    '-moz-osx-font-smoothing': 'grayscale',
  },
  code: {
    fontFamily: `Menlo, 'Droid Sans Mono', monospace`,
  },
  weights: fontWeights,
  sizes: {
    base: 16,
    inputs: '16px',
    xs: '12px',
    sm: '14px',
    md: '16px',
    lg: '26px',
    xl: '36px',
  },
};

const baseBorders = {
  standard: {
    borderWidth: '1px',
    borderRadius: '8px',
    borderStyle: 'solid',
  },
  inner: {
    borderWidth: '1px',
    borderRadius: '4px',
    borderStyle: 'solid',
    borderColor: 'rgba(0,0,0,0.1)',
  },
  innerThick: {
    borderWidth: '2px',
    borderRadius: '4px',
    borderStyle: 'solid',
  },
  standardThick: {
    borderWidth: '2px',
    borderRadius: '8px',
    borderStyle: 'solid',
  },
  standardInvisible: {
    borderWidth: '0',
    borderRadius: '7px',
    borderStyle: 'solid',
  },
  innerInvisible: {
    borderWidth: '0',
    borderRadius: '4px',
    borderStyle: 'solid',
  },
  standardLight: {
    borderWidth: '1px',
    borderRadius: '8px',
    borderStyle: 'solid',
    borderColor: 'rgba(0,0,0,0.1)',
  },
};

const borders = {
  base: baseBorders,
  light: {
    ...baseBorders,
    standard: {
      ...baseBorders.standard,
      borderColor: '#ddd',
    },
  },
  dark: {
    ...baseBorders,
    standard: {
      ...baseBorders.standard,
      borderColor: '#555',
    },
  },
};

const backdrops = {
  light: {
    allIn: urls.backdrops.allIn,
  },
  dark: {
    allIn: urls.backdrops.allInDark,
  },
};

const shadows = {
  none: {
    'box-shadow': 'none',
  },
  standard: {
    'box-shadow': '0px 0px 12px 0px rgba(0,0,0,0.08)',
  },
  standardRaised: {
    'box-shadow': '0px 0px 12px 0px rgba(0,0,0,0.15)',
  },
  standardCentered: {
    'box-shadow': '0px 0px 12px 0px rgba(0,0,0,0.06)',
  },
  textStandard: {
    'text-shadow': '0px 2px 4px rgba(0,0,0,0.28)',
  },
  textStandardRaised: {
    'text-shadow': '0px 3px 6px rgba(0,0,0,0.15)',
  },
};

// Only used in boxed widgets
const size = {
  0: {
    width: `100%`,
    height: bm(10),
  },
  1: {
    width: bm(10),
    height: bm(10),
  },
  2: {
    width: `${parseInt(bm(10 * 2)) + parseInt(bm(1 * 2))}px`,
    height: `${parseInt(bm(10 * 2)) + parseInt(bm(1 * 2))}px`,
  },
};

// Iterate through the sizes and create a media template
const media = Object.keys(screenSizes).reduce((acc, label) => {
  acc[label] = (...args) => css`
    @media (${label !== 'larger' ? 'max' : 'min'}-width: ${screenSizes[label]}px) {
      ${css(...args)};
    }
  `;
  return acc;
}, {});

const measurements = {
  gutters: bm(0.5),
};

const animation = {
  timingFast: '150ms',
  timingSlow: '250ms',
  timingFunction: 'cubic-bezier(0.2, 0.0, 0, 1.0);',
  timingFunctionIn: 'cubic-bezier(0.05, 0.7, 0.1, 1.0)',
  timingFunctionOut: 'cubic-bezier(0.3, 0.0, 0.8, 0.15)',
};

const getTheme = (variant) => ({
  variant,
  brandColors: brandColors[variant],
  borders: borders[variant],
  backdrops: backdrops[variant],
  margins,
  boxWidths,
  shadows,
  rhythm,
  fonts,
  screenSizes,
  media,
  urls,
  size,
  measurements,
  animation,
});

export {
  urls,
  rhythm,
  backdrops,
  brandColors,
  borders,
  margins,
  boxWidths,
  shadows,
  screenSizes,
  media,
  fonts,
  getTheme,
  bm,
  size,
  measurements,
  animation,
};
